import { silentLoginUrl } from '../api/urls'

/** Потолок ожидания: Keycloak `prompt=none` и два редиректа обычно укладываются в секунды. */
export const SILENT_LOGIN_TIMEOUT_MS = 12_000

let inFlight: Promise<boolean> | null = null

export interface SilentLoginOptions {
  /** Куда BFF вернёт, если ответ не iframe-контракта; по умолчанию — корень. */
  returnUrl?: string
  timeoutMs?: number
  /** Окно и документ подставляются в тестах. */
  window?: Window
}

/**
 * Тихий вход по живой SSO-сессии через СКРЫТЫЙ iframe: человек остаётся на
 * странице, форма входа не показывается. BFF отвечает страницей, которая шлёт
 * `postMessage({ type: 'oauth_silent_complete', success })` на origin приложения.
 * `true` — cookie-сессия поднята; `false` — SSO-сессии нет, сервер отказал или
 * ответ не пришёл за `timeoutMs`. Сообщения чужих origin игнорируются.
 * Параллельные вызовы схлопываются в один iframe.
 */
export function runSilentLogin(options: SilentLoginOptions = {}): Promise<boolean> {
  inFlight ??= attempt(options).finally(() => {
    inFlight = null
  })
  return inFlight
}

function attempt(options: SilentLoginOptions): Promise<boolean> {
  const win = options.window ?? (typeof window === 'undefined' ? undefined : window)
  if (win === undefined) return Promise.resolve(false)
  const doc = win.document
  const timeoutMs = options.timeoutMs ?? SILENT_LOGIN_TIMEOUT_MS

  return new Promise<boolean>((resolve) => {
    const frame = doc.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.tabIndex = -1
    frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden'
    let settled = false

    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      win.removeEventListener('message', onMessage)
      win.clearTimeout(timer)
      frame.remove()
      resolve(ok)
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== win.location.origin) return
      const data = event.data as { type?: unknown; success?: unknown } | null
      if (data?.type !== 'oauth_silent_complete') return
      finish(data.success === true)
    }
    const timer = win.setTimeout(() => finish(false), timeoutMs)

    win.addEventListener('message', onMessage)
    frame.src = silentLoginUrl(options.returnUrl ?? '/')
    doc.body.appendChild(frame)
  })
}
